import { saveToStorage, getFromStorage } from './storage';
import { Score } from '../types';

interface ScoreEntry {
  playerId: string;
  hole: number;
  strokes: number;
  timestamp: number;
  synced: boolean;
}

interface TournamentScores {
  tournamentId: string;
  scores: Record<string, ScoreEntry[]>;
  lastSyncTimestamp: number;
}

export class ScoreManager {
  private static instance: ScoreManager;
  
  private constructor() {}
  
  static getInstance(): ScoreManager {
    if (!ScoreManager.instance) {
      ScoreManager.instance = new ScoreManager();
    }
    return ScoreManager.instance;
  }
  
  saveScore(tournamentId: string, playerId: string, hole: number, strokes: number): boolean {
    try {
      const tournamentScores = this.getTournamentScores(tournamentId);
      
      // Initialize player scores if needed
      if (!tournamentScores.scores[playerId]) {
        tournamentScores.scores[playerId] = [];
      }
      
      // Find existing score for this hole
      const existingIndex = tournamentScores.scores[playerId].findIndex(
        score => score.hole === hole
      );
      
      const newScore: ScoreEntry = {
        playerId,
        hole,
        strokes,
        timestamp: Date.now(),
        synced: false
      };
      
      if (existingIndex >= 0) {
        // Update existing score
        tournamentScores.scores[playerId][existingIndex] = newScore;
      } else {
        // Add new score
        tournamentScores.scores[playerId].push(newScore);
      }
      
      // Save updated scores
      return saveToStorage(`scores_${tournamentId}`, tournamentScores);
    } catch (error) {
      console.error('Failed to save score:', error);
      return false;
    }
  }
  
  getPlayerScores(tournamentId: string, playerId: string): ScoreEntry[] {
    const tournamentScores = this.getTournamentScores(tournamentId);
    return tournamentScores.scores[playerId] || [];
  }
  
  getTournamentScores(tournamentId: string): TournamentScores {
    const key = `scores_${tournamentId}`;
    const existing = getFromStorage<TournamentScores>(key);
    
    if (existing) {
      return existing;
    }
    
    // Return empty structure if nothing exists
    return {
      tournamentId,
      scores: {},
      lastSyncTimestamp: 0
    };
  }
  
  markScoresAsSynced(tournamentId: string, playerId: string, holeNumbers: number[]): boolean {
    try {
      const tournamentScores = this.getTournamentScores(tournamentId);
      
      if (!tournamentScores.scores[playerId]) {
        return true; // Nothing to sync
      }
      
      // Mark specified holes as synced
      tournamentScores.scores[playerId] = tournamentScores.scores[playerId].map(score => {
        if (holeNumbers.includes(score.hole)) {
          return { ...score, synced: true };
        }
        return score;
      });
      
      tournamentScores.lastSyncTimestamp = Date.now();
      
      return saveToStorage(`scores_${tournamentId}`, tournamentScores);
    } catch (error) {
      console.error('Failed to mark scores as synced:', error);
      return false;
    }
  }
  
  getUnsyncedScores(tournamentId: string): Record<string, ScoreEntry[]> {
    const tournamentScores = this.getTournamentScores(tournamentId);
    const unsynced: Record<string, ScoreEntry[]> = {};
    
    Object.entries(tournamentScores.scores).forEach(([playerId, scores]) => {
      const playerUnsynced = scores.filter(score => !score.synced);
      if (playerUnsynced.length > 0) {
        unsynced[playerId] = playerUnsynced;
      }
    });
    
    return unsynced;
  }
}

// Convenience functions
export const saveScore = (tournamentId: string, playerId: string, hole: number, strokes: number): boolean => {
  return ScoreManager.getInstance().saveScore(tournamentId, playerId, hole, strokes);
};

export const getPlayerScores = (tournamentId: string, playerId: string): ScoreEntry[] => {
  return ScoreManager.getInstance().getPlayerScores(tournamentId, playerId);
};

export const markScoresAsSynced = (tournamentId: string, playerId: string, holeNumbers: number[]): boolean => {
  return ScoreManager.getInstance().markScoresAsSynced(tournamentId, playerId, holeNumbers);
};

export const getUnsyncedScores = (tournamentId: string): Record<string, ScoreEntry[]> => {
  return ScoreManager.getInstance().getUnsyncedScores(tournamentId);
}; 